import React, { useState, useEffect } from 'react';
import { Wrench, Plus, Edit2, Trash2, Clock, DollarSign, Scale, X } from 'lucide-react';
import { API_URL } from '../../services/api';

const TYPE_OPTIONS = [
  { value: 'FIXE',   label: 'Prix fixe',    icon: DollarSign, color: 'green',  desc: 'Coût fixe par opération (ex: 7 DH)' },
  { value: 'POIDS',  label: 'Prix au poids', icon: Scale,      color: 'orange', desc: 'Coût par gramme d\'or travaillé (ex: 12 DH/g)' },
  { value: 'MINUTE', label: 'Prix horaire',  icon: Clock,      color: 'blue',   desc: 'Coût par minute de travail (ex: 2 DH/min)' },
];

const EMPTY_FORM = { nom: '', cout: '', type_cout: 'FIXE', type_ressource: 'externe', ordre: '' };

function GestionComposantsFabrication() {
  const [composants, setComposants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadComposants(); }, []);

  const loadComposants = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/devis/couts-fabrication`);
      const data = await r.json();
      setComposants(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ nom: c.nom, cout: c.cout, type_cout: c.type_cout || 'FIXE', type_ressource: c.type_ressource || 'externe', ordre: c.ordre ?? '' });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditingId(null); setForm(EMPTY_FORM); };

  const handleSave = async () => {
    if (!form.nom.trim() || form.cout === '') { alert('Nom et coût requis'); return; }
    setSaving(true);
    try {
      const url = editingId
        ? `${API_URL}/devis/couts-fabrication/${editingId}`
        : `${API_URL}/devis/couts-fabrication`;
      const r = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nom: form.nom.trim(), cout: parseFloat(form.cout), type_cout: form.type_cout, type_ressource: form.type_ressource, ordre: form.ordre !== '' ? parseInt(form.ordre) : 99 }),
      });
      if (r.ok) { closeModal(); await loadComposants(); }
      else { const d = await r.json(); alert(d.error || 'Erreur'); }
    } catch (e) { alert('Erreur réseau'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, nom) => {
    if (!window.confirm(`Désactiver "${nom}" ?`)) return;
    await fetch(`${API_URL}/devis/couts-fabrication/${id}`, { method: 'DELETE' });
    await loadComposants();
  };

  const getTypeMeta = (type) => TYPE_OPTIONS.find(t => t.value === type) || TYPE_OPTIONS[0];

  const getTypeBadge = (type) => {
    const meta = getTypeMeta(type);
    const Icon = meta.icon;
    const colors = {
      green:  'bg-green-100 text-green-800',
      orange: 'bg-orange-100 text-orange-800',
      blue:   'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`${colors[meta.color]} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit`}>
        <Icon size={13} /> {meta.label}
      </span>
    );
  };

  const getUnite = (type) => {
    if (type === 'MINUTE') return 'par minute';
    if (type === 'POIDS')  return 'DH/g';
    return 'prix fixe';
  };

  if (loading) return <div className="p-6 text-center">Chargement...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Wrench className="text-indigo-600" size={36} />
              Composants de Fabrication
            </h1>
            <p className="text-gray-600 mt-1">Gérez les étapes et coûts de fabrication</p>
          </div>
          <button onClick={openCreate}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center gap-2 shadow-lg">
            <Plus size={20} /> Nouveau Composant
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {TYPE_OPTIONS.map(opt => {
            const Icon = opt.icon;
            const borderColors = { green: 'border-green-500', orange: 'border-orange-500', blue: 'border-blue-500' };
            const bgColors    = { green: 'bg-green-100',    orange: 'bg-orange-100',    blue: 'bg-blue-100' };
            const iconColors  = { green: 'text-green-600',  orange: 'text-orange-600',  blue: 'text-blue-600' };
            return (
              <div key={opt.value} className={`bg-white rounded-xl shadow-sm p-6 border-l-4 ${borderColors[opt.color]}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 font-medium">{opt.label}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">
                      {composants.filter(c => c.type_cout === opt.value).length}
                    </p>
                  </div>
                  <div className={`${bgColors[opt.color]} p-3 rounded-lg`}>
                    <Icon className={iconColors[opt.color]} size={24} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tableau */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-lg font-semibold text-gray-900">Liste des Composants</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-gray-600 uppercase w-12">#</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Nom</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase">Coût</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Statut</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {composants.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">{c.ordre ?? '—'}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="bg-indigo-100 p-2 rounded-lg mr-3">
                          <Wrench className="text-indigo-600" size={18} />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{c.nom}</div>
                          <div className="text-xs text-gray-400">ID: {c.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-center gap-1">
                        {getTypeBadge(c.type_cout)}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          c.type_ressource === 'interne'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {c.type_ressource === 'interne' ? '🏭 Interne' : '🔗 Externe'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="text-lg font-bold text-gray-900">{parseFloat(c.cout).toFixed(2)} DH</div>
                      <div className="text-xs text-gray-400">{getUnite(c.type_cout)}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${c.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {c.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openEdit(c)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                          <Edit2 size={17} />
                        </button>
                        <button onClick={() => handleDelete(c.id, c.nom)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                          <Trash2 size={17} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {composants.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <Wrench className="mx-auto h-10 w-10 mb-3 opacity-40" />
                <p>Aucun composant</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal créer/éditer */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? 'Modifier' : 'Nouveau'} Composant
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X size={22} />
              </button>
            </div>

            {/* Nom */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Nom</label>
              <input
                type="text"
                value={form.nom}
                onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                placeholder="Ex: Polissage, Sertissage..."
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-400 focus:outline-none"
              />
            </div>

            {/* Ordre d'affichage */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Ordre d'affichage <span className="text-gray-400 font-normal">(1 = en premier dans le devis)</span></label>
              <input
                type="number" min="1" max="99"
                value={form.ordre}
                onChange={e => setForm(f => ({ ...f, ordre: e.target.value }))}
                placeholder="Ex: 1, 2, 3..."
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-400 focus:outline-none"
              />
            </div>

            {/* Type de tarification */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type de tarification</label>
              <div className="grid grid-cols-3 gap-2">
                {TYPE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  const active = form.type_cout === opt.value;
                  const activeColors = {
                    green:  'bg-green-600 border-green-600 text-white',
                    orange: 'bg-orange-500 border-orange-500 text-white',
                    blue:   'bg-blue-600 border-blue-600 text-white',
                  };
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setForm(f => ({ ...f, type_cout: opt.value }))}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-sm font-semibold ${
                        active ? activeColors[opt.color] : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Icon size={20} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">{getTypeMeta(form.type_cout).desc}</p>
            </div>

            {/* Type ressource interne / externe */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type de ressource</label>
              <div className="flex gap-2">
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, type_ressource: 'interne' }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                    form.type_ressource === 'interne'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                  }`}
                >
                  🏭 Interne (coef main d'œuvre)
                </button>
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, type_ressource: 'externe' }))}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                    form.type_ressource === 'externe'
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300'
                  }`}
                >
                  🔗 Externe (coef composants)
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {form.type_ressource === 'interne'
                  ? 'Applique le coefficient main d\'œuvre (ex: ×3.0) dans le calcul du devis'
                  : 'Applique le coefficient composants (ex: ×1.3) dans le calcul du devis'}
              </p>
            </div>

            {/* Coût */}
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Coût ({form.type_cout === 'MINUTE' ? 'DH/minute' : form.type_cout === 'POIDS' ? 'DH/gramme' : 'DH fixe'})
              </label>
              <div className="relative">
                <input
                  type="number" step="0.01" min="0"
                  value={form.cout}
                  onChange={e => setForm(f => ({ ...f, cout: e.target.value }))}
                  placeholder="0.00"
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:border-indigo-400 focus:outline-none pr-16"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">
                  {form.type_cout === 'MINUTE' ? 'DH/min' : form.type_cout === 'POIDS' ? 'DH/g' : 'DH'}
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={closeModal}
                className="flex-1 py-2.5 border-2 border-gray-200 rounded-lg text-gray-600 font-semibold hover:bg-gray-50">
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Sauvegarde...' : (editingId ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionComposantsFabrication;
